import { useQueryClient } from '@tanstack/react-query'
import {
  userAuthQueryFetcher as createUserAuthQueryFetcher,
  userAuthQueryKey as createUserAuthQueryKey,
  userQueryFetcher as createUserQueryFetcher,
  userQueryKey as createUserQueryKey,
  userQueryRootKey as generatedUserQueryRootKey,
  useUserAuthQuery as useUserAuthQueryBase,
  useUserQuery as useUserQueryBase,
} from '../gen/user'
import type {
  UserAuthQuery,
  UserAuthQueryVariables,
  UserQueryVariables,
  UserQuery,
} from '../gen/user'
import ms from 'ms'
import { getUserIdSelector, isLoggedInQuerySelector } from '../selectors'

interface UseIsLoggedInQueryOptions {
  enabled?: boolean
}

export function useIsLoggedInQuery(options?: UseIsLoggedInQueryOptions) {
  return useUserAuthQuery(undefined, {
    ...options,
    select: isLoggedInQuerySelector,
  })
}

export function useIsAuthenticated() {
  const { data } = useIsLoggedInQuery()

  return Boolean(data)
}

export function useSetUserQuery() {
  const queryClient = useQueryClient()

  function setUserQuery(user: UserQuery) {
    queryClient.setQueryData<UserQuery>(createUserQueryKey(), user)
  }

  return { setUserQuery }
}

export function useUserIdQuery() {
  return useUserAuthQuery(undefined, {
    select: getUserIdSelector,
  })
}

export const userAuthQueryOptions = {
  staleTime: ms('20s'),
}

export function useUserAuthQuery<D = UserAuthQuery>(
  variables?: UserAuthQueryVariables,
  options?: {
    select?: (data: UserAuthQuery) => D
  },
) {
  return useUserAuthQueryBase(variables, {
    ...userAuthQueryOptions,
    ...options,
  })
}

export function getUserAuthFetcher(headers?: RequestInit['headers']) {
  return createUserAuthQueryFetcher(undefined, { headers })
}

export const userAuthQueryFetcher = createUserAuthQueryFetcher()
export const userAuthQueryKey = createUserAuthQueryKey()

export const userQueryOptions = {
  staleTime: ms('20s'),
}

type UseUserQueryOptions<D> = NonNullable<Parameters<typeof useUserQueryBase<D>>[1]>

export function useUserQuery<D = UserQuery>(
  variables?: UserQueryVariables,
  options?: Omit<UseUserQueryOptions<D>, 'select'> & {
    select?: (data: UserQuery) => D
  },
) {
  return useUserQueryBase(variables, {
    ...userQueryOptions,
    ...options,
  })
}

export const userQueryRootKey = generatedUserQueryRootKey
export const userQueryKey = createUserQueryKey()
export const userQueryFetcher = createUserQueryFetcher()
